import { FC, useEffect, useRef, useState, useTransition } from 'react';
import { RouteComponentProps, useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import Form from 'src/app/common/components/Form';

import { ModulePermissionProps } from 'src/app/common/module/types';
import {
  fetchQRCodeActivityCategoryById,
  createQRCodeActivityCategory,
  updateQRCodeActivityCategory,
} from '../../../network/activityTrackingCrud';
import { QRCodeActivityCategory, EStatus } from '../../../types/activity-tracking-types';
import {
  ACTIVITY_TRACKING_BASE_PATH,
  QR_CODE_ACTIVITY_CATEGORY_PATH,
  FRAMEWORK_OPTIONS,
  HOST_BY_OPTIONS,
  channelRoleMatrix,
} from '../../../constans';

type QRCodeActivityCategoryDetailPageProps = RouteComponentProps &
  ModulePermissionProps & {
    mode: 'create' | 'edit' | 'view';
  };

const useStyles = makeStyles()(() => ({
  container: {
    padding: '24px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    borderLeft: '4px solid #DE1127',
    paddingLeft: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3f4254',
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
  },
  formLabel: {
    width: '180px',
    minWidth: '180px',
    fontWeight: 500,
    fontSize: '14px',
    color: '#3f4254',
  },
  required: {
    color: '#DE1127',
  },
  formInput: {
    flex: 1,
  },
  errorText: {
    color: '#F018A6',
    fontSize: '12px',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
}));

const QRCodeActivityCategoryDetailPage: FC<QRCodeActivityCategoryDetailPageProps> = ({
  mode,
  enableCreate,
  enableUpdate,
}) => {
  const { classes } = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const Translation = (msgId: string) => intl.formatMessage({ id: msgId });
  const [isPending, startTransition] = useTransition();
  const abortControllerRef = useRef<AbortController | null>(null);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const publishRef = useRef(false);

  const initialValues: Partial<QRCodeActivityCategory> = {
    name: '',
    framework: '',
    accessibility: 'Internal',
    status: EStatus.InActive,
    hostByChannel: '',
    hostByRoles: [],
    sortOrder: '',
  };

  const backPath = `${ACTIVITY_TRACKING_BASE_PATH}${QR_CODE_ACTIVITY_CATEGORY_PATH}/list`;

  const getRoleOptionsByChannel = (channel?: string): string[] => {
    if (!channel || channel.length === 0) {
      return [];
    }
    return channelRoleMatrix[channel] ?? [];
  };

  const handleChannelChange = (channel: string) => {
    form.setFieldsValue({
      hostByChannel: channel,
      hostByRoles: [],
    });
  };

  useEffect(() => {
    if ((isEdit || isView) && id) {
      setLoading(true);
      fetchQRCodeActivityCategoryById(id, dispatch)
        .then((data) => {
          form.setFieldsValue({
            name: data.name ?? '',
            framework: data.framework ?? '',
            accessibility: data.accessibility ?? 'Internal',
            status: data.status ?? EStatus.InActive,
            hostByChannel: data.hostByChannel ?? '',
            hostByRoles: data.hostByRoles ?? [],
            sortOrder: data.sortOrder ?? '',
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [dispatch, form, id, isEdit, isView]);

  const onFinish = async (values: Partial<QRCodeActivityCategory>) => {
    const publish = publishRef.current;
    setSaving(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const payload: Partial<QRCodeActivityCategory> = {
        ...values,
        status: publish ? EStatus.Active : EStatus.InActive,
        sortOrder:
          values.sortOrder !== undefined && values.sortOrder !== null && String(values.sortOrder).length > 0
            ? Number(values.sortOrder)
            : null,
      };

      const response = isCreate
        ? await createQRCodeActivityCategory(payload, undefined, controller.signal)
        : isEdit && id
        ? await updateQRCodeActivityCategory(id, payload, undefined, controller.signal)
        : Promise.resolve({ data: null, isSuccess: false, error: { message: 'ID_NOT_PROVIDED' } });

      if (!response.isSuccess) {
        const error = response.error;
        if (error?.code === 'SORT_ORDER_DUPLICATE_MESSAGE') {
          form.setFields([
            {
              name: 'sortOrder',
              errors: ['Display Order number are used. Please input another number.'],
            },
          ]);
          return;
        }
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: Translation('activityTracking.notify.error'),
            },
          ]),
        );
        return;
      }

      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: isCreate
              ? `${Translation('activityTracking.notify.createSuccess')} - ${values.name}`
              : `${Translation('activityTracking.notify.updateSuccess')} - ${values.name}`,
          },
        ]),
      );
      history.push(backPath);
    } catch (error: unknown) {
      const { message, ...rest } = error as any;
      if (message === 'SORT_ORDER_DUPLICATE_MESSAGE') {
        form.setFields([
          {
            name: 'sortOrder',
            errors: ['Display Order number are used. Please input another number.'],
          },
        ]);
      } else {
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: Translation('activityTracking.notify.error'),
            },
          ]),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = isCreate
    ? Translation('activityTracking.qrCodeActivityCategory.createTitle')
    : isEdit
    ? Translation('activityTracking.qrCodeActivityCategory.editTitle')
    : Translation('activityTracking.qrCodeActivityCategory.viewTitle');

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Header */}
      <div className={classes.header}>
        <span className={classes.title}>{pageTitle}</span>
        <Button variant="contained" color="inherit" size="small" onClick={() => history.push(backPath)}>
          {Translation('app.button.back')}
        </Button>
      </div>

      <Form form={form} initialValues={initialValues} onFinish={onFinish}>
        {/* Category Name */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>
            Category Name
            <span className={classes.required}> *</span>
          </div>
          <div className={classes.formInput}>
            <Form.Item
              name="name"
              required={false}
              rules={[
                {
                  required: true,
                  message: Translation('activityTracking.validation.mandatoryField'),
                },
              ]}
            >
              {({ value, onChange }) => (
                <TextField
                  fullWidth
                  placeholder={Translation('app.input.placeholder.please-input')}
                  inputProps={{ maxLength: 50 }}
                  disabled={isView}
                  variant="outlined"
                  size="small"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
            </Form.Item>
          </div>
        </div>

        {/* Framework */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>
            {Translation('activityTracking.qrCodeActivityCategory.framework')}
            <span className={classes.required}> *</span>
          </div>
          <div className={classes.formInput}>
            <Form.Item
              name="framework"
              required={false}
              rules={[
                {
                  required: true,
                  message: Translation('activityTracking.validation.mandatoryField'),
                },
              ]}
            >
              {({ value, onChange }) => (
                <FormControl size="small" variant="outlined">
                  <Select
                    displayEmpty
                    disabled={isView}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                      width: 180,
                    }}
                    renderValue={(val) =>
                      val ? (
                        String(val)
                      ) : (
                        <span style={{ color: '#aaa' }}>{Translation('app.input.placeholder.please-select')}</span>
                      )
                    }
                  >
                    {FRAMEWORK_OPTIONS.filter((opt) => opt.value !== '').map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Form.Item>
          </div>
        </div>

        {/* Accessibility */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>
            {Translation('activityTracking.qrCodeActivityCategory.accessibility')}
            <span className={classes.required}> *</span>
          </div>
          <div className={classes.formInput}>
            <Form.Item name="accessibility" required={false}>
              <RadioGroup row>
                <FormControlLabel
                  value="Internal"
                  control={<Radio color="secondary" disabled={isView} />}
                  label={Translation('activityTracking.qrCodeActivityCategory.accessibility.internal')}
                />
              </RadioGroup>
            </Form.Item>
          </div>
        </div>

        {/* Host By */}
        <div
          className={classes.formRow}
          style={{
            alignItems: 'flex-start',
          }}
        >
          <div
            className={classes.formLabel}
            style={{
              minWidth: 100,
              width: 100,
            }}
          >
            {Translation('activityTracking.qrCodeActivityCategory.hostBy')}
            <span className={classes.required}> *</span>
          </div>
          <div
            className={classes.formInput}
            style={{
              paddingTop: 50,
            }}
          >
            {/* Channel */}
            <div className={classes.formRow}>
              <div
                className={classes.formLabel}
                style={{
                  minWidth: 80,
                  width: 80,
                }}
              >
                Channel
                <span className={classes.required}> *</span>
              </div>
              <Form.Item
                name="hostByChannel"
                required={false}
                rules={[
                  {
                    required: true,
                    message: 'Please select a channel',
                  },
                ]}
              >
                {({ value, onChange }) => (
                  <FormControl size="small" variant="outlined">
                    <Select
                      displayEmpty
                      disabled={isView}
                      style={{
                        width: 180,
                      }}
                      value={value}
                      onChange={(e) => {
                        handleChannelChange(e.target.value as string);
                      }}
                      renderValue={(val) =>
                        val ? (
                          HOST_BY_OPTIONS.find((opt) => opt.value === val)?.displayName || String(val)
                        ) : (
                          <span style={{ color: '#aaa' }}>{Translation('app.input.placeholder.please-select')}</span>
                        )
                      }
                    >
                      {HOST_BY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Form.Item>
            </div>
            {/* Roles */}
            <Form.Item shouldUpdate noStyle>
              {(_control, _meta, { getFieldValue }) => {
                const selectedChannel = (getFieldValue('hostByChannel') as string) || '';
                const roleOptions = getRoleOptionsByChannel(selectedChannel);
                if (roleOptions.length === 0) {
                  return null;
                }

                return (
                  <div
                    className={classes.formRow}
                    style={{
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      className={classes.formLabel}
                      style={{
                        minWidth: 80,
                        width: 80,
                      }}
                    >
                      Roles
                      <span className={classes.required}> *</span>
                    </div>
                    <Form.Item
                      name="hostByRoles"
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: 'Please select at least one role',
                          type: 'array',
                        },
                      ]}
                    >
                      {(control) => {
                        const selectedRoles = Array.isArray(control.value) ? control.value : [];

                        return (
                          <FormGroup>
                            {roleOptions.map((role) => {
                              const checked = selectedRoles.includes(role);
                              return (
                                <FormControlLabel
                                  key={role}
                                  label={role}
                                  control={
                                    <Checkbox
                                      color="secondary"
                                      disabled={isView}
                                      checked={checked}
                                      onChange={(_event, isChecked) => {
                                        const nextRoles = isChecked
                                          ? [...selectedRoles, role]
                                          : selectedRoles.filter((item: string) => item !== role);
                                        control.onChange(nextRoles);
                                      }}
                                    />
                                  }
                                />
                              );
                            })}
                          </FormGroup>
                        );
                      }}
                    </Form.Item>
                  </div>
                );
              }}
            </Form.Item>
          </div>
        </div>

        {/* Display Order */}
        <div className={classes.formRow}>
          <div className={classes.formLabel}>Display Order</div>
          <div className={classes.formInput}>
            <Form.Item
              name="sortOrder"
              required={false}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    return Number(value) >= 1 && Number(value) <= 1000
                      ? Promise.resolve()
                      : Promise.reject(new Error('Display Order must be between 1 and 1000'));
                  },
                },
              ]}
            >
              {({ value, onChange }) => (
                <TextField
                  fullWidth
                  placeholder={Translation('app.input.placeholder.please-input')}
                  disabled={isView}
                  type="number"
                  variant="outlined"
                  size="small"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
            </Form.Item>
          </div>
        </div>

        {/* Actions */}
        {!isView && (enableCreate || enableUpdate) && (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                startTransition(() => {
                  publishRef.current = false;
                  form.submit();
                });
              }}
              disabled={saving || isPending}
            >
              {saving ? <CircularProgress size={20} /> : Translation('app.button.save')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                startTransition(() => {
                  publishRef.current = true;
                  form.submit();
                });
              }}
              disabled={saving || isPending}
            >
              {saving ? <CircularProgress size={20} /> : Translation('activityTracking.action.saveAndPublish')}
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default QRCodeActivityCategoryDetailPage;
